import { cleanPayloadOfCustodianData } from './util';
import {
  accountTypes,
  createRPSRolloverData,
  rolloverTypes,
  autoSplitPercents,
  getAppSourceType,
  secondaryCommitMessage,
  updateRIWithholding,
  applicationStatusTypes,
  createFormURL,
  isRetirementIncomeFundPresent
} from '../shared/utils';
import {
  pollForSubmissionResponse,
  commitConsent,
  getUserExistingAgreements,
  loadUser,
  initiateAuthService
} from '../shared/loaders';
import { convertAppDataToCheckRemittanceFormData } from '../shared/services/util';
import { autoSplitPercents as autoSplitPercentsForChosenInvestments } from '../../hooks/useCartState';
import {
  analyticsDataLayer,
  dispatchPageLoad
} from '../../analytics/analytics-helper';
import { isEmpty, mergeTyped } from '../../core/util';

import { unlockRewards } from '../shared/summit.utils';
import { DataLoader } from '../../core/types/data-loader';
import {
  ApplicationState,
  PartialStateStore,
  StateStore,
  StateStoreOrVoid
} from '../../core/types/application-state';

import { createRolloverApplicationService } from '../shared/services/mutualfund-rollover';
import { getFundAllocationsService } from '../shared/services/allocation-service';
import { postCheckRemittanceFormData } from '../shared/services/check-remittance.service';
import {
  accountTypesAnalytics,
  productTypesAnalytics
} from '../../analytics/util';
import { getExistingAgreementsService } from '../shared/services/existing-agreements-service';
import { encode_deviceprint as encodeDeviceprint } from '../../rsa-fingerprint';
import { retrieveFilterAndAssignAppID } from '../shared/services/profile-service';

export const commitRolloverType: DataLoader = ({ store, chosen }) => {
  const {
    amount,
    app: { newInvestments = [] },
    chosenInvestments,
    loadedData: { closedFunds = [] } = {}
  } = store;

  const closedFundTickers = closedFunds.map(f => f?.ticker);

  const newInvestmentsWithoutClosedFunds = autoSplitPercents(
    newInvestments.filter(fund => !closedFundTickers.includes(fund?.ticker)),
    amount
  );

  const chosenInvestmentsWithoutClosedFunds = autoSplitPercentsForChosenInvestments(
    chosenInvestments.filter(fund => !closedFundTickers.includes(fund?.ticker)),
    amount
  );

  switch (chosen) {
    case rolloverTypes.DIRECT:
      return {
        ...store,
        app: { ...store.app },
        rolloverType: chosen,
        loadedData: {
          closedTickers: []
        }
      };
    case rolloverTypes.INDIRECT:
      return {
        ...store,
        app: { ...store.app },
        formerEmployerName: undefined,
        rolloverType: chosen,
        loadedData: {
          closedTickers: closedFunds
        }
      };
    case 'BYOP':
      return {
        ...store,
        app: {
          ...store.app,
          newInvestments: newInvestmentsWithoutClosedFunds
        },
        chosenInvestments: chosenInvestmentsWithoutClosedFunds,
        rolloverType: rolloverTypes.INDIRECT,
        loadedData: {
          closedTickers: []
        }
      };
    default:
      return {
        ...store,
        app: { ...store.app },
        rolloverType: chosen,
        loadedData: {
          closedTickers: closedFunds
        }
      };
  }
};

export const loadClosedFunds: DataLoader = async ({
  store
}): Promise<StateStore> => {
  const {
    app: { newInvestments = [], existingAccounts = [] },
    summitData: { assetTierID = 0 } = {}
  } = store;

  const { closedFunds: closedFundReward } = unlockRewards(assetTierID);

  const newInvestmentTickers = newInvestments.map(f => f?.ticker);
  // account for existing funds being invested in as well
  // these are separate from new investments; necessary for submission purposes
  const existingInvestmentTickers = existingAccounts.map(
    f => f?.investmentVehicle?.ticker
  );

  const tickers = newInvestmentTickers.concat(existingInvestmentTickers);

  const funds = await getFundAllocationsService({ tickers });

  const closedFunds = !closedFundReward
    ? funds.filter(mf => !mf.openToNewInvestors)
    : [];

  return {
    app: {},
    loadedData: { closedFunds }
  };
};

export const loadInvestmentDetails: DataLoader = async ({
  store
}): Promise<StateStore> => {
  const {
    app: { existingAccounts = [], newInvestments = [] },
    rolloverType,
    authenticated
  } = store;

  // account for the scenario where customer is using exisiting agreement(s)
  // these will be stored for submission in existingAccounts
  const tickers: any = [];
  if (existingAccounts.length > 0) {
    existingAccounts.map(f => tickers.push(f.investmentVehicle?.ticker));
  }
  if (newInvestments.length > 0) {
    newInvestments.map(f => tickers.push(f.ticker));
  }
  const fundDetails =
    authenticated && (await getFundAllocationsService({ tickers }));

  // Bypasses loading check remit form when not needed
  if (rolloverType === rolloverTypes.DIRECT)
    return {
      app: {},
      loadedData: { fundDetails }
    };

  const payload = convertAppDataToCheckRemittanceFormData(store, fundDetails);
  const blob = await postCheckRemittanceFormData(payload);
  const remittanceFormURL = createFormURL(blob);

  return {
    app: {},
    loadedData: { fundDetails, remittanceFormURL }
  };
};

export const loadExistingAgreements = async (): Promise<PartialStateStore> => {
  const existingAgreementsResults = await getExistingAgreementsService([
    accountTypes.ROLLOVER
  ]);

  return {
    ...(existingAgreementsResults && {
      existingAgreements: existingAgreementsResults
    })
  };
};

export const commitCustodian: DataLoader = ({ chosen, inputs }): StateStore => {
  const { custodianID, custodianName } = chosen;
  const { formerEmployerName } = inputs;

  return {
    app: {
      custodian: {
        custodianName,
        custodianID
      }
    },
    formerEmployerName
  };
};

export const loadLoginData: DataLoader = async (): Promise<StateStoreOrVoid> => {
  const authDetails = await initiateAuthService();

  const queryParams = window.location.search;
  return {
    authenticated: authDetails?.authenticated,
    accountType: accountTypes.ROLLOVER,
    ...(!isEmpty(queryParams) && {
      routeData: {
        queryString: queryParams
      }
    }),
    app: {}
  };
};

export const makeLoadProfileData = (queryParams): DataLoader =>
  async function loadProfileData(
    a: ApplicationState
  ): Promise<StateStoreOrVoid> {
    let existingProfileAppID = '';
    const userResults = await loadUser(a);
    const rpsData = createRPSRolloverData(queryParams);

    const existingAgreementsResults = await getUserExistingAgreements(
      userResults,
      [accountTypes.ROLLOVER]
    );

    if (
      userResults?.app?.customer?.primaryEmailAddress &&
      userResults?.authenticated
    ) {
      existingProfileAppID = await retrieveFilterAndAssignAppID(
        userResults?.app?.customer?.primaryEmailAddress,
        accountTypes.ROLLOVER
      );
    }

    // return object needs to be in this order
    // so existingAgreementsResults.app does not overwrite userResults.app
    return {
      ...(existingAgreementsResults && {
        existingAgreements: existingAgreementsResults
      }),
      ...mergeTyped<StateStoreOrVoid>(userResults, {
        app: {
          profileApplicationID: existingProfileAppID
        },
        accountType: accountTypes.ROLLOVER,
        ...(rpsData?.rpsDirectRollover && rpsData)
      })
    };
  };

/* eslint-disable complexity */
export const createApplication: DataLoader = async ({
  store,
  inputs,
  chosen
}): Promise<StateStoreOrVoid> => {
  if (
    [
      'byop',
      'multiple',
      'existing_only',
      'rdf',
      'funding',
      'info',
      'contact'
    ].includes(chosen)
  ) {
    return { inReviewMode: true };
  }

  const {
    app,
    estimatedRolloverAmount,
    rolloverType,
    formerEmployerName,
    consumerName,
    chosenInvestments
  } = store;
  const { customer, termsIDs, newInvestments } = app;
  const { taxWithholdingOption, taxWithholdingPercent = 0 } = inputs;
  // use terms ids to create consent
  await commitConsent(termsIDs, customer);
  // terms ids should be an array of strings for service
  const stringTermsIDs = termsIDs?.map(id => id?.toString());

  // check for Retirement Income and tax withholding
  const hasRetrimentIncomeInvestment = isRetirementIncomeFundPresent(
    chosenInvestments
  );

  const finalizedNewInvestments = hasRetrimentIncomeInvestment
    ? updateRIWithholding(
        newInvestments,
        taxWithholdingOption,
        taxWithholdingPercent,
        chosenInvestments
      )
    : newInvestments;

  // add rollover type and term IDs to account creation service payload
  let updatedAppState = {
    ...app,
    newInvestments: finalizedNewInvestments,
    estimatedRolloverAmount,
    rolloverType,
    formerEmployerName,
    termsIDs: stringTermsIDs,
    deviceToken: encodeDeviceprint()
  };

  // in case custodian data exist when Indirect Rollover
  // delete this data as submission will fail with it
  if (rolloverType === rolloverTypes.INDIRECT) {
    updatedAppState = cleanPayloadOfCustodianData(updatedAppState);
  }

  // get the service error code and tracking id on the initial submission request
  const {
    serviceErrorCode,
    trackingIdentifier
  } = await createRolloverApplicationService(updatedAppState, consumerName);

  const isSubmissionTimedOut = serviceErrorCode === 504;

  if (isSubmissionTimedOut) {
    return {
      loadedData: {
        is504Timeout: true
      },
      app: {
        applicationStatus: applicationStatusTypes.COMPLETED
      }
    };
  }

  const submissionResult = await pollForSubmissionResponse(trackingIdentifier);
  return submissionResult;
};
/* eslint-enable complexity */

// specifies loading message when creating account at end
createApplication.message = 'Creating your new account';
createApplication.secondaryMessage = secondaryCommitMessage;

export const updateAnalytics: DataLoader = async (
  { store },
  flowProps
): Promise<StateStoreOrVoid> => {
  const { formerEmployerName = '', rolloverType = 'DEFAULT' } = store;
  const subType = formerEmployerName.match(/^internal-[0-9]*$/)
    ? 'INTERNAL'
    : rolloverType;
  analyticsDataLayer({
    accountType: accountTypesAnalytics.ROLLOVER[subType],
    productType: productTypesAnalytics.MUTUAL_FUND,
    sourceType: getAppSourceType(store?.consumerName),
    userType:
      store?.authenticated && !store?.loadedData?.newUserCreated
        ? 'Existing'
        : 'New',
    amount: store?.amount,
    isComplete: Boolean(flowProps?.analytics?.isComplete),
    clientTier: store?.summitData?.assetTierID?.toString()
  });
  dispatchPageLoad(flowProps?.analytics.pageName);
};
